"use client";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { motion } from "framer-motion";
import Link from "next/link";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { generateBreadcrumbList } from "@/lib/structured-data";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { getBlogPostsData } from "./blog-list";

// Metadata is handled in the metadata.ts file

// Generate structured data for the blog page
function generateBlogStructuredData() {
  const breadcrumbs = [
    { name: 'Home', item: '/' },
    { name: 'Blog', item: '/blog' },
  ];
  
  return {
    breadcrumbs: generateBreadcrumbList(breadcrumbs),
  };
}

// Get structured data for the blog page
const structuredData = generateBlogStructuredData();

interface BlogPost {
  id: number;
  title: string;
  description: string;
  date: string;
  readTime: string;
  image: string;
  slug: string;
  author?: {
    name: string;
    title: string;
    image: string;
  };
  keywords?: string[];
}

function BlogPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  // Get blog posts from the blog-list data
  const blogPostsData = getBlogPostsData();
  
  // Localize all blog post data
  const blogPosts = blogPostsData.map(post => ({
    ...post,
    title: localize(post.title),
    description: localize(post.description)
  }));
  
  // Sort blog posts by date in descending order (newest first)
  const sortedBlogPosts = [...blogPosts].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12" itemScope itemType="https://schema.org/Blog">
      {/* Add structured data to the page */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData.breadcrumbs) }}
      />
      <div className="text-center space-y-4 mb-16 mt-8">
        <h1 className="text-4xl md:text-5xl font-bold mb-4" itemProp="name">
          {localize("Latest Articles")}
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          {localize("Insights and updates from the world of AI and technology")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {sortedBlogPosts.map((post: BlogPost, index) => (
          <motion.div
            key={post.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <Card className="group overflow-hidden h-full" style={{
              borderColor: '#8B5CF6'
            }}>
              <Link href={`/${currentLocale}/blog/${post.slug}`} className="block h-full">
                <CardHeader className="p-0">
                  <div className="relative h-[220px] overflow-hidden">
                    <OptimizedImage
                      src={post.image}
                      alt={post.title}
                      priority={index < 2}
                      className="object-cover transition-transform duration-500 group-hover:scale-105 h-full w-full"
                      style={{
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex justify-between items-center mb-2 text-sm text-muted-foreground">
                    <span>
                      {new Date(post.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    <span>{post.readTime}</span>
                  </div>
                  <CardTitle className="text-xl mb-2 group-hover:text-primary transition-colors">
                    {post.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-3">
                    {post.description}
                  </CardDescription>
                  <div className="mt-4 flex justify-end">
                    <span className="text-primary font-medium group-hover:underline">
                      {localize("Read more")}
                    </span>
                  </div>
                </CardContent>
              </Link>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default BlogPage;
